import { useEffectEvent } from "../../lib/use-effect-event";
"use client";

// UIArc — source MIT (c) 2026 Elia Kuratli, arc-library@7238f981 registry/components/carousel/carousel.tsx.
// Import fidèle (ui/components) : imports canoniques Foundry, jetons --arc-* (outil U1 arc_tokens). Voir UI-BANK.md.
import { Children, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactNode, Ref } from "react";
import { AnimatePresence, animate, motion, motionValue, useReducedMotionConfig as useReducedMotion, useTransform } from "motion/react";
import type { MotionValue, TargetAndTransition } from "motion/react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./carousel.module.css";
// Lot U9 (mandat Root additif) : le transport de piste est factorisé dans `./transport`, partagé avec CoverFlow ; ce fichier
// garde la présentation (diapositives, indicateurs, rotation) et ses comportements, à l'identique. Fidelity Médias (06/10/2026) :
// indice contrôlé autoritaire et annonce de l'indice adopté seulement (réserves U9 levées dans le transport, voir TRANSPORT.md).
import { useSlideTransport } from "./transport";

/**
 * A row of slides people browse by dragging, flicking, a trackpad swipe, the arrow keys, or the controls below it. Use it for a short,
 * ordered set of peers such as products or case studies, where the neighbours stay in view as a hint that there is more.
 * The active slide sits in the center, its neighbours shrink and dim with distance, and the page indicator stretches into a pill that
 * travels with the slides frame by frame.
 */
export interface CarouselProps {
  /** Names the carousel for assistive technology, for example "From the Harbour suites". */
  label: string;
  /** One child per slide. */
  children: ReactNode;
  /** Controlled active slide. Pair with `onIndexChange`. */
  index?: number;
  defaultIndex?: number;
  onIndexChange?: (index: number) => void;
  /** Width of one slide as a CSS length. Use `cqw` for a share of the carousel width. */
  slideSize?: string;
  /** Names each slide and its indicator. Defaults to "2 of 5". */
  slideLabel?: (index: number, count: number) => string;
  /** Milliseconds per slide. Adds a play control that rotates through the slides; rotation pauses on hover, keyboard focus, and drag, and is unavailable with reduced motion. */
  interval?: number;
  /** Starts rotating on mount. Off by default, so motion begins with the viewer. */
  autoplay?: boolean;
  className?: string;
}

/** Width of a resting dot and of the active pill, in px. */
const DOT = 6, PILL = 22;
/** Scale and opacity of a slide one full step from the center. */
const SCALE = .9, DIM = .5;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const defaultSlideLabel = (index: number, count: number) => `${index + 1} of ${count}`;
const subscribeNothing = () => () => {};

const iconIn: TargetAndTransition = { opacity: 0, scale: .6, filter: `blur(${motionTokens.blur.subtle}px)` };
const iconRest: TargetAndTransition = { opacity: 1, scale: 1, filter: "blur(0px)" };
const iconOut: TargetAndTransition = { ...iconIn, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } };
const iconEnter = { ...motionTokens.spring.snappy, opacity: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.enter] }, filter: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.enter] } } as const;

export function Carousel({ label, children, index: controlledIndex, defaultIndex = 0, onIndexChange, slideSize = "min(80cqw, 340px)", slideLabel = defaultSlideLabel, interval, autoplay = false, className }: CarouselProps) {
  const slides = Children.toArray(children);
  const count = slides.length;
  const last = Math.max(0, count - 1);
  const id = useId();
  const reduced = useReducedMotion() ?? false;
  const hydrated = useSyncExternalStore(subscribeNothing, () => true, () => false);
  const [ownIndex, setOwnIndex] = useState(() => clamp(defaultIndex, 0, last));
  const index = clamp(controlledIndex ?? ownIndex, 0, last);
  const [startIndex] = useState(index);
  const [playing, setPlaying] = useState(autoplay);
  const [hovered, setHovered] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  // One drain per indicator, so a pill that just ran out keeps its state while it shrinks away.
  const drains = useMemo(() => Array.from({ length: count }, () => motionValue(0)), [count]);
  const rootRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const playRef = useRef<HTMLButtonElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const metrics = useRef({ step: 0, width: 0 });

  function slideAt(clientX: number) {
    const nodes = Array.from(trackRef.current?.children ?? []);
    const hit = nodes.findIndex(node => { const rect = node.getBoundingClientRect(); return clientX >= rect.left && clientX <= rect.right; });
    return hit < 0 ? null : hit;
  }
  // Fidelity Médias (06/10/2026) : une demande n'est annoncée — et ne garde le focus d'onglet — que si l'indice est adopté. En mode
  // contrôlé, le parent fait autorité (`authoritative`) : un refus ramène la piste, l'indicateur et l'ARIA sur son indice.
  const pending = useRef<{ to: number; announce: boolean; tab: boolean } | null>(null);
  const transport = useSlideTransport({
    count, index, reduced, metrics, viewportRef, hitTest: slideAt,
    onSelect: (next, announce) => { pending.current = { to: next, announce, tab: false }; if (controlledIndex === undefined) setOwnIndex(next); onIndexChange?.(next); },
    // One drain per indicator, so a pill that just ran out keeps its state while it shrinks away.
    onPosition: position => drains.forEach((drain, i) => { if (i !== transport.target.current && Math.abs(position - i) >= 1 && drain.get() !== 0) drain.jump(0); }),
    controlled: controlledIndex !== undefined,
    authoritative: true,
  });
  const { x, progress, target, travel, drag, dragging, glide, select } = transport;
  const canRotate = Boolean(interval) && count > 1 && !(hydrated && reduced);
  const running = canRotate && playing && !hovered && !focusPaused && !dragging && inView && pageVisible;

  const measure = useEffectEvent(() => {
    const track = trackRef.current, viewport = viewportRef.current;
    const [first, second] = Array.from(track?.children ?? []) as HTMLElement[];
    if (!track || !viewport || !first) return;
    // Centers are unaffected by each slide's scale, and rects keep the sub-pixel step that offsetLeft would round away.
    const center = (node: HTMLElement) => { const rect = node.getBoundingClientRect(); return rect.left + rect.width / 2; };
    const step = second ? center(second) - center(first) : first.offsetWidth;
    const changed = step !== metrics.current.step;
    metrics.current = { step, width: viewport.clientWidth };
    // The server positions the start slide with CSS; from here on the motion value owns the track.
    track.dataset.measured = "";
    if (changed && !drag.current) { travel.current?.stop(); travel.current = null; x.jump(-target.current * step); progress.set(target.current); }
  });

  useLayoutEffect(() => {
    measure();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);


  const followIndexProp = useEffectEvent((next: number) => { if (target.current !== next) { target.current = next; glide(next); } });
  useEffect(() => { followIndexProp(index); }, [index]);
  // Le rendu qui suit une demande dit si elle est adoptée (indice reçu = demandé) ou refusée (le transport demande ce rendu).
  useEffect(() => {
    const request = pending.current;
    if (!request) return;
    pending.current = null;
    if (request.to === index) { if (request.announce) setAnnouncement(slideLabel(index, count)); return; }
    if (request.tab && document.activeElement === tabRefs.current[request.to]) tabRefs.current[index]?.focus({ preventScroll: true });
  });

  const advance = useEffectEvent(() => select(index >= last ? 0 : index + 1, { announce: false }));
  useEffect(() => {
    if (!running || !interval) return;
    const drain = drains[index];
    if (!drain) return;
    const controls = animate(drain, 1, { duration: (1 - drain.get()) * interval / 1000, ease: "linear", onComplete: () => advance() });
    return () => controls.stop();
  }, [running, index, interval, drains]);

  useEffect(() => {
    const root = rootRef.current;
    if (!interval || !root) return;
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => { if (entry) setInView(entry.isIntersecting); });
    observer?.observe(root);
    const onVisibility = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => { observer?.disconnect(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [interval]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.altKey || event.ctrlKey || event.metaKey || count < 2) return;
    const node = event.target as HTMLElement;
    if (node.closest("input, textarea, select, [contenteditable]")) return;
    const from = target.current;
    const to = event.key === "ArrowLeft" ? from - 1 : event.key === "ArrowRight" ? from + 1 : event.key === "Home" ? 0 : event.key === "End" ? last : null;
    if (to === null) return;
    event.preventDefault();
    const next = clamp(to, 0, last);
    select(next);
    if (node.getAttribute("role") === "tab") { tabRefs.current[next]?.focus({ preventScroll: true }); if (pending.current) pending.current.tab = true; }
    else if (trackRef.current?.contains(node)) viewportRef.current?.focus({ preventScroll: true });
  }

  function toggleRotation() {
    const drain = drains[index];
    if (!playing) { setPlaying(true); return; }
    setPlaying(false);
    // Pausing refills the pill: this slide stays until the viewer moves on.
    if (drain) { if (reduced) drain.jump(0); else animate(drain, 0, { duration: motionTokens.duration.standard, ease: [...motionTokens.ease.standard] }); }
  }

  const rootStyle = { "--carousel-slide": slideSize, "--carousel-start": startIndex } as CSSProperties;

  return <section ref={rootRef} className={[styles.root, className].filter(Boolean).join(" ")} style={rootStyle} aria-roledescription="carousel" aria-label={label} onKeyDown={onKeyDown}
    onFocus={event => setFocusPaused(event.target !== playRef.current && event.target.matches(":focus-visible"))}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusPaused(false); }}>
    <div ref={viewportRef} className={styles.viewport} role="group" aria-label="Diapositives" tabIndex={0} data-dragging={dragging || undefined}
      onPointerDown={transport.handlers.onPointerDown} onPointerMove={transport.handlers.onPointerMove} onPointerUp={transport.handlers.onPointerUp} onPointerCancel={transport.handlers.onPointerCancel}
      onPointerEnter={event => { if (event.pointerType === "mouse") setHovered(true); }} onPointerLeave={() => setHovered(false)}
      onClickCapture={transport.handlers.onClickCapture}
      onDragStart={event => event.preventDefault()}>
      <motion.div ref={trackRef} className={styles.track} style={{ x }}>
        {slides.map((slide, i) => <Slide key={isValidElement(slide) && slide.key != null ? slide.key : i} id={`${id}-slide-${i}`} index={i} progress={progress} active={i === index} label={slideLabel(i, count)}>{slide}</Slide>)}
      </motion.div>
    </div>
    {count > 1 && <div className={styles.controls}>
      {canRotate && <button ref={playRef} type="button" className={styles.control} aria-label={playing ? "Mettre le défilement en pause" : "Lancer le défilement"} onClick={toggleRotation}>
        <span className={styles.iconSlot}><AnimatePresence initial={false}>
          <motion.span key={playing ? "pause" : "play"} className={styles.icon} initial={reduced ? { opacity: 0 } : iconIn} animate={iconRest} exit={reduced ? { opacity: 0, transition: { duration: motionTokens.duration.instant } } : iconOut} transition={reduced ? { duration: motionTokens.duration.instant } : iconEnter}>
            {playing ? <Pause size={15} strokeWidth={1.75} fill="currentColor" aria-hidden="true" /> : <Play size={15} strokeWidth={1.75} fill="currentColor" aria-hidden="true" />}
          </motion.span>
        </AnimatePresence></span>
      </button>}
      <div className={styles.tabs} role="tablist" aria-label="Choisir une diapositive">
        {/* Une valeur d'écoulement par diapositive (`drains` mémorisé sur `slides.length`) : itérée directement. */}
        {drains.map((drain, i) => <Dot key={i} index={i} last={last} progress={progress} drain={drain} selected={i === index} label={slideLabel(i, count)} controls={`${id}-slide-${i}`}
          tabRef={node => { tabRefs.current[i] = node; }} onSelect={() => { select(i); if (pending.current) pending.current.tab = true; }} />)}
      </div>
      <div className={styles.arrows}>
        <button type="button" className={styles.control} aria-label="Diapositive précédente" aria-disabled={index === 0} onClick={() => { if (target.current > 0) select(target.current - 1); }}><ChevronLeft size={18} strokeWidth={1.75} aria-hidden="true" /></button>
        <button type="button" className={styles.control} aria-label="Diapositive suivante" aria-disabled={index === last} onClick={() => { if (target.current < last) select(target.current + 1); }}><ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" /></button>
      </div>
    </div>}
    <span className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">{announcement}</span>
  </section>;
}

/** A slide shrinks and dims with its distance from the center, read straight from the track position so it never lags the finger. */
function Slide({ id, index, progress, active, label, children }: { id: string; index: number; progress: MotionValue<number>; active: boolean; label: string; children: ReactNode }) {
  const scale = useTransform(progress, [index - 1, index, index + 1], [SCALE, 1, SCALE]);
  const opacity = useTransform(progress, [index - 1, index, index + 1], [DIM, 1, DIM]);
  return <motion.div id={id} className={styles.slide} role="tabpanel" aria-roledescription="slide" aria-label={label} inert={!active} style={{ scale, opacity }}>{children}</motion.div>;
}

/** Each indicator grows toward a pill as the track nears its slide, so the pill hands over continuously instead of hopping. */
function Dot({ index, last, progress, drain, selected, label, controls, tabRef, onSelect }: { index: number; last: number; progress: MotionValue<number>; drain: MotionValue<number>; selected: boolean; label: string; controls: string; tabRef: Ref<HTMLButtonElement>; onSelect: () => void }) {
  const emphasis = useTransform(progress, value => Math.max(0, 1 - Math.abs(clamp(value, 0, last) - index)));
  const width = useTransform(emphasis, value => DOT + (PILL - DOT) * value);
  return <button ref={tabRef} type="button" role="tab" className={styles.tab} aria-selected={selected} aria-controls={controls} aria-label={label} tabIndex={selected ? 0 : -1} onClick={onSelect}>
    <motion.span className={styles.dot} style={{ width }} aria-hidden="true">
      <motion.span className={styles.dotFill} style={{ opacity: emphasis }} />
      <motion.span className={styles.dotDrain} style={{ scaleX: drain }} />
    </motion.span>
  </button>;
}

export default Carousel;
